import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AlertTriangle, CheckCircle2, ChevronDown, Loader2, Zap } from "lucide-react";
import { Button } from "../../../../components/tailwind/button";
import { Label } from "../../../../components/tailwind/label";
import {
  fetchTriggerConfigRequest,
  fetchWorkflowListRequest,
  saveTriggerConfigRequest
} from "../../../../store/slices/workflow";
import { workflowTheme } from "../../utils/workflowBuilderTheme";
import {
  buildDefaultTriggerConfigPayload,
  getDefaultTriggerFromConfig,
  getLiveWorkflows
} from "../../utils/workflowListUtils";
import { waBtn } from "../../utils/whatsappUiTokens";
import { cn } from "../../../../lib/utils";
import { WaBone, WaCardBone } from "../skeletons/WhatsAppPageSkeletons";

function formatWorkflowOptionLabel(workflow, isActiveTrigger) {
  const name = workflow.name || workflow.workflowId;
  const version = workflow.activeVersion;
  const prefix = isActiveTrigger ? "✓ " : "";
  return `${prefix}${name} (v${version})`;
}

export function WorkflowDefaultTriggerSettings() {
  const dispatch = useDispatch();
  const { workflows, loading: listLoading, error: listError } = useSelector(
    (state) => state.workflowList || {}
  );
  const workflowTrigger = useSelector((state) => state.workflowTrigger || {});

  const liveWorkflows = useMemo(() => getLiveWorkflows(workflows), [workflows]);
  const activeTrigger = useMemo(
    () => getDefaultTriggerFromConfig(workflowTrigger.config),
    [workflowTrigger.config]
  );
  const activeTriggerWorkflowId = activeTrigger?.workflowId ?? "";

  const [selectedWorkflowId, setSelectedWorkflowId] = useState("");
  const [selectionTouched, setSelectionTouched] = useState(false);

  useEffect(() => {
    dispatch(fetchWorkflowListRequest({ operation: "LIST_WORKFLOWS", payload: {} }));
    dispatch(fetchTriggerConfigRequest({ operation: "GET_TRIGGER_CONFIG", payload: {} }));
  }, [dispatch]);

  useEffect(() => {
    if (selectionTouched || !activeTriggerWorkflowId) return;
    setSelectedWorkflowId(activeTriggerWorkflowId);
  }, [activeTriggerWorkflowId, selectionTouched]);

  useEffect(() => {
    if (selectionTouched || activeTriggerWorkflowId || liveWorkflows.length !== 1) return;
    setSelectedWorkflowId(liveWorkflows[0].workflowId);
  }, [liveWorkflows, activeTriggerWorkflowId, selectionTouched]);

  const selectedWorkflow = liveWorkflows.find(
    (workflow) => workflow.workflowId === selectedWorkflowId
  );
  const activeTriggerWorkflow = workflows?.find(
    (workflow) => workflow.workflowId === activeTriggerWorkflowId
  );
  const isAlreadyActiveTrigger = !!selectedWorkflowId
    && selectedWorkflowId === activeTriggerWorkflowId;
  const canSetTrigger = !!selectedWorkflow?.activeVersion && !isAlreadyActiveTrigger;

  const handleSetTrigger = () => {
    if (!selectedWorkflow?.workflowId || !selectedWorkflow.activeVersion) return;
    dispatch(
      saveTriggerConfigRequest({
        operation: "SAVE_TRIGGER_CONFIG",
        payload: buildDefaultTriggerConfigPayload(
          selectedWorkflow.workflowId,
          selectedWorkflow.activeVersion
        )
      })
    );
  };

  const pageLoading = listLoading || workflowTrigger.configLoading;
  const pageError = listError || workflowTrigger.configError;

  return (
    <div className={`${workflowTheme.card} rounded-xl border shadow-sm overflow-hidden`}>
      <div className={`px-6 py-5 border-b ${workflowTheme.borderSubtle} ${workflowTheme.surfaceMuted}`}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-emerald-50 dark:bg-emerald-950/50 flex items-center justify-center border border-[#9FE1CB] dark:border-emerald-800">
            <Zap className="w-5 h-5 text-[#0F6E56] dark:text-emerald-400" />
          </div>
          <div>
            <h2 className={`text-lg font-semibold ${workflowTheme.textPrimary}`}>
              Default trigger workflow
            </h2>
            <p className={`text-sm ${workflowTheme.textSecondary} mt-0.5`}>
              Choose which live workflow runs when a customer starts a WhatsApp conversation.
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-5">
        {pageLoading && (
          <div className="space-y-4" aria-busy="true" aria-label="Loading trigger settings">
            <WaCardBone className="space-y-3">
              <WaBone className="h-4 w-32" />
              <WaBone className="h-10 w-full rounded-lg" />
              <WaBone className="h-10 w-full rounded-lg" />
            </WaCardBone>
          </div>
        )}
        {!pageLoading && pageError && (
          <p className="text-sm text-red-600 dark:text-red-400">{pageError}</p>
        )}

        {!pageLoading && !pageError && activeTriggerWorkflowId && (
          <div className={`flex items-center gap-2 rounded-lg border ${workflowTheme.border} ${workflowTheme.surfaceMuted} px-4 py-3`}>
            <CheckCircle2 className="w-5 h-5 text-[#0F6E56] dark:text-emerald-400 shrink-0" />
            <div>
              <p className={`text-sm font-medium ${workflowTheme.textPrimary}`}>
                Active trigger
              </p>
              <p className={`text-sm ${workflowTheme.textSecondary}`}>
                {activeTriggerWorkflow?.name || activeTriggerWorkflowId}
                {activeTrigger?.workflowVersion != null && ` (v${activeTrigger.workflowVersion})`}
              </p>
            </div>
          </div>
        )}

        {!pageLoading && !pageError && (
          <>
            <div className="space-y-2">
              <Label
                className={`text-xs font-semibold ${workflowTheme.textSecondary} uppercase tracking-wide`}
              >
                Workflow
              </Label>
              <div className="relative">
                <select
                  className={`w-full h-10 rounded-md border ${workflowTheme.border} ${workflowTheme.input} px-3 pr-8 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#0F6E56] appearance-none disabled:opacity-60`}
                  value={selectedWorkflowId}
                  onChange={(event) => {
                    setSelectionTouched(true);
                    setSelectedWorkflowId(event.target.value);
                  }}
                  disabled={liveWorkflows.length === 0}
                >
                  <option value="">— Select a live workflow —</option>
                  {liveWorkflows.map((workflow) => (
                    <option key={workflow.workflowId} value={workflow.workflowId}>
                      {formatWorkflowOptionLabel(
                        workflow,
                        workflow.workflowId === activeTriggerWorkflowId
                      )}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
              {liveWorkflows.length === 0 && (
                <p className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                  No live workflows available. Publish a workflow first.
                </p>
              )}
            </div>

            <div className="flex justify-end">
              {isAlreadyActiveTrigger ? (
                <Button
                  type="button"
                  variant="outline"
                  className="min-w-[140px] border-[#9FE1CB] dark:border-emerald-800 text-[#0F6E56] dark:text-emerald-400"
                  disabled
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Already set
                </Button>
              ) : (
                <Button
                  type="button"
                  className={cn("min-w-[140px]", waBtn.primary)}
                  onClick={handleSetTrigger}
                  disabled={!canSetTrigger || workflowTrigger.loading}
                >
                  {workflowTrigger.loading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Zap className="w-4 h-4 mr-2" />
                  )}
                  Set as trigger
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
